import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { businessApiService } from "../services/businessApiService";
import {
  fromAppointmentRulesDtoToAppointmentRules,
  fromUpdateAppointmentRulesRequestToDto,
  type AppointmentRules,
  type UpdateAppointmentRulesRequest,
} from "../types/appointmentRuleTypes";
import { businessKeys } from "./businessQueries";
import { useCurrentBusiness } from "./useCurrentBusiness";

export function useAppointmentRules(businessId: string) {
  const { user } = useAuth();
  const { business } = useCurrentBusiness();
  return useQuery({
    queryKey: businessKeys.appointmentRules(businessId, user?.id),
    queryFn: async ({ signal }) => {
      const dto = await businessApiService.getAppointmentRules(businessId, signal);
      signal.throwIfAborted();
      const rules = fromAppointmentRulesDtoToAppointmentRules(dto);
      if (rules.businessId !== businessId) throw new Error("Appointment rules not found in this business.");
      return rules;
    },
    enabled: !!user && !!businessId && business?.id === businessId && business.ownerId === user.id,
    staleTime: 5 * 60_000,
  });
}

export function useUpdateAppointmentRules(businessId: string) {
  const queryClient = useQueryClient();
  const { user, getAuthGeneration } = useAuth();
  const { business } = useCurrentBusiness();
  return useMutation<AppointmentRules, Error, UpdateAppointmentRulesRequest, { generation: number; userId: string }>({
    onMutate: async () => {
      if (!user || business?.id !== businessId || business.ownerId !== user.id) {
        throw new Error("Only the selected business owner can change appointment rules.");
      }
      const scope = { generation: getAuthGeneration(), userId: user.id };
      await queryClient.cancelQueries({ queryKey: businessKeys.appointmentRules(businessId, user.id) });
      if (scope.generation !== getAuthGeneration()) throw new Error("Your account changed. Reopen these settings.");
      return scope;
    },
    mutationFn: async (request) => {
      const dto = fromUpdateAppointmentRulesRequestToDto(request);
      const rules = fromAppointmentRulesDtoToAppointmentRules(await businessApiService.updateAppointmentRules(businessId, dto));
      if (rules.businessId !== businessId) throw new Error("Appointment rules not found in this business.");
      return rules;
    },
    onSuccess: async (rules, _request, scope) => {
      if (!scope || scope.generation !== getAuthGeneration()) return;
      await queryClient.cancelQueries({ queryKey: businessKeys.appointmentRules(businessId, scope.userId) });
      if (scope.generation !== getAuthGeneration()) return;
      queryClient.setQueryData(businessKeys.appointmentRules(businessId, scope.userId), rules);
      await queryClient.invalidateQueries({ queryKey: ["availableTimeSlots", { businessId, userId: scope.userId }] });
    },
  });
}
